/**
 * @file Canonical config-backed Ordering list helpers.
 */

export const MAX_ORDERING_LISTS = 50;

/**
 * Returns whether a value is a valid Ordering list identifier.
 *
 * @param {*} value Candidate identifier.
 * @returns {boolean} True for a non-blank string identifier.
 */
function hasOrderingListId(value) {
    return typeof value === "string" && Boolean(value.trim());
}

/**
 * Returns the configured Ordering lists in stable visual order.
 * Invalid legacy/config-corrupt entries are excluded from operational use.
 *
 * @param {object | null | undefined} config Runtime configuration.
 * @returns {Array<object>} Ordering list records.
 */
export function getOrderingLists(config) {
    const lists = Array.isArray(config?.orderingLists) ?
        config.orderingLists : [];
    return lists
        .filter((list) => list && typeof list === "object" &&
            hasOrderingListId(list.id))
        .map((list, index) => ({
            ...list,
            id: String(list.id).trim(),
            order: Number.isFinite(Number(list.order)) ? Number(list.order) : index,
            isActive: list.isActive === true,
            hasBeenUsed: list.hasBeenUsed === true,
        }))
        .sort((left, right) => {
            const orderDifference = left.order - right.order;
            if (orderDifference !== 0) {return orderDifference;}
            return left.id.localeCompare(right.id);
        });
}

/**
 * Returns board-visible Ordering lists.
 *
 * @param {object | null | undefined} config Runtime configuration.
 * @returns {Array<object>} Active Ordering lists.
 */
export function getActiveOrderingLists(config) {
    return getOrderingLists(config).filter((list) => list.isActive === true);
}

/**
 * Returns a user-safe Settings validation error for a proposed list array.
 * Required display-name validation remains owned by the Settings form flow.
 *
 * @param {*} value Proposed `config/main.orderingLists` value.
 * @returns {string | null} Error message, or null when structurally valid.
 */
export function getOrderingListsValidationError(value) {
    if (!Array.isArray(value)) {
        return "Ordering lists must be saved as a list of configured records.";
    }
    if (value.length > MAX_ORDERING_LISTS) {
        return `Ordering supports at most ${MAX_ORDERING_LISTS} lists.`;
    }

    const ids = new Set();
    for (const list of value) {
        if (!list || typeof list !== "object" || !hasOrderingListId(list.id)) {
            return "Each Ordering list needs a stable identifier.";
        }
        if (typeof list.name !== "string") {
            return "Each Ordering list needs a display name.";
        }
        if (!Number.isFinite(Number(list.order))) {
            return "Each Ordering list needs a visual order.";
        }
        if (typeof list.isActive !== "boolean") {
            return "Each Ordering list needs an active or inactive state.";
        }
        if (Object.hasOwn(list, "hasBeenUsed") &&
            typeof list.hasBeenUsed !== "boolean") {
            return "Each Ordering list usage state must be true or false.";
        }
        const id = String(list.id).trim();
        if (ids.has(id)) {
            return "Each Ordering list must have its own stable identifier.";
        }
        ids.add(id);
    }
    return null;
}
